import { useWebRTC } from '@/entities/webrtc'

import { getDomExceptionErrorType, MediaDevicesErrorType } from '@/shared/composables/dom-exception-error-type'
import { resolveIsCallOnHold } from '@/shared/store/hold'
import { sendDTMFMediaConstraintsToSession } from '@/shared/utils/jssip'

import { activateLocalCameraStreamAtPrivateSession, activateLocalScreenSharingAtPrivateSession, deactivateLocalVideoStream, closePictureInPicture } from './local-media'

const getPrivateSessionFacade = () => {
  const facade = useWebRTC().currentRTCSessionFacade.value
  if (!facade) {
    throw new Error('No session')
  }

  return facade
}

const isPrivateCallOnHold = () => {
  const facade = useWebRTC().currentRTCSessionFacade.value
  if (!facade) {
    return false
  }

  return resolveIsCallOnHold({
    sessionId: facade.sessionId,
    isGroup: false,
    facadeIsOnHold: Boolean(facade.isOnHold.value),
  })
}

export const switchCameraInPrivateSession = async () => {
  const facade = getPrivateSessionFacade()
  const { isVideoMuted, isScreenSharing, isVideoSharing, localVideoStream, localScreenStream } = facade
  const isVideoMutedNow = !isVideoSharing.value
  const isScreenActiveNow = isScreenSharing.value

  // WUI-5282: do not start camera while held (DTMF/media path unreliable). Allow stop.
  if (isVideoMutedNow && isPrivateCallOnHold()) {
    return
  }

  try {
    if (isVideoMutedNow) {
      if (isScreenActiveNow) {
        closePictureInPicture()
        deactivateLocalVideoStream(localScreenStream, facade.session.connection)
      }
      await activateLocalCameraStreamAtPrivateSession(facade.session, localVideoStream)
    } else {
      deactivateLocalVideoStream(localVideoStream, facade.session.connection)
    }
    isVideoMuted.value = !isVideoMutedNow
    return sendDTMFMediaConstraintsToSession(
      facade.session,
      { video: isVideoMutedNow },
    )
  } catch (error) {
    return Promise.reject(error)
  }
}

export const switchScreenSharingInPrivateSession = async () => {
  const facade = getPrivateSessionFacade()
  const { isScreenSharing, isVideoSharing, isVideoMuted, localScreenStream, localVideoStream } = facade
  const isScreenMutedNow = !isScreenSharing.value
  const isVideoActiveNow = isVideoSharing.value

  // WUI-5282: do not start share while held. Allow stop / track-ended cleanup.
  if (isScreenMutedNow && isPrivateCallOnHold()) {
    return
  }

  try {
    if (isScreenMutedNow) {
      // WUI-5960: video→share is replaceTrack only — no Signal DTMF (593/592).
      // Remote already has video:true; notifying would blank their display.
      if (isVideoActiveNow) {
        deactivateLocalVideoStream(localVideoStream, facade.session.connection)
        isVideoMuted.value = true
      }
      await activateLocalScreenSharingAtPrivateSession(facade.session, localScreenStream)
      const track = localScreenStream.value?.getVideoTracks().at(0)
      if (track) {
        track.addEventListener('ended', () => {
          const activeFacade = useWebRTC().currentRTCSessionFacade.value
          const currentTrack = activeFacade?.localScreenStream.value?.getVideoTracks().at(0)
          if (!currentTrack || currentTrack.id !== track.id) {
            return
          }
          switchScreenSharingInPrivateSession()
        }, { once: true })
      }
    } else {
      closePictureInPicture()
      deactivateLocalVideoStream(localScreenStream, facade.session.connection)
    }
    isVideoMuted.value = true
    // Cam was already on: remote video flag stays true — skip DTMF.
    // Cam was off / stop share: still need 592 (on) or 593 (off).
    if (isScreenMutedNow && isVideoActiveNow) {
      return true
    }
    return sendDTMFMediaConstraintsToSession(
      facade.session,
      { video: isScreenMutedNow },
    )
  } catch (error: unknown) {
    if (error instanceof DOMException) {
      const errorType = getDomExceptionErrorType(error)
      if (errorType === MediaDevicesErrorType.UserPermissionDenied && isVideoActiveNow) {
        switchCameraInPrivateSession()
      }
    }

    return Promise.reject(error)
  }
}
